import React, {useState} from 'react';
import {NavLink, useLocation} from 'react-router-dom';
import cn from 'classnames';
import {useDispatch, useSelector} from 'react-redux';
import {FormattedMessage} from 'react-intl';

import {IStore} from 'redux/interface';
import {changeLocale, loadTranslation} from 'redux/translation/translationAsyncActions';

import {languages} from 'utils/constants/constants';
import {RoutesUrl, routeTranslationKeys} from 'utils/constants/routeConstants';

import ChatImg from 'assets/image/menu-auth/jun_chatroom.png';
import JournalImg from 'assets/image/menu-auth/jun_journal.png';
import ReviewImg from 'assets/image/menu-auth/jun_reviews.png';
import FavoritesImg from 'assets/image/menu-auth/jun_favorites.png';
import LibraryImg from 'assets/image/menu-auth/jun_library.png';
import ResourceImg from 'assets/image/menu-auth/jun_resources.png';
import ChatroomImg from 'assets/image/menu-auth/jun_chatroom.png';
import language from 'assets/image/header/language-heart.svg';

import '../styles.scss';
import {Journals} from '../../../pages/journals/Journals';
import {startReloginAction} from '../../../redux/auth/actions';

export const LanguageMenu = (): JSX.Element => {
  const [isOpenLanguageMenu, setIsOpenLanguageMenu] = useState(false);
  const dispatch = useDispatch();

  const {
    translation: {locale}
  } = useSelector((state: IStore) => state);

  function handleChangeLocale(value: string) {
    return () => {
      if (isOpenLanguageMenu) {
        setIsOpenLanguageMenu(false);
      } else {
        setIsOpenLanguageMenu(true);
      }
      dispatch(changeLocale(value));
    };
  }

  const sortLang = languages.reduce<{
    value: string;
    title: string;
    key: string;
  }[]>((acc, item) => {
    if (item.value === locale) {
      return [item, ...acc];
    }
    acc.push(item);

    return acc;
  }, []);

  return <div className="header__language">
    <div className="header__language-wrapper">
      <div
        className={cn('language', {
          'language--open': isOpenLanguageMenu
        })}
      >
        {
          sortLang
            .map((lang, i) => {
              return <button
                key={i}
                className="language__title"
                onClick={handleChangeLocale(lang.value)}
              >{lang.title}</button>;
            })
        }
      </div>
    </div>
    <div className="header__language-item">
      <img className="header__language-item-icon" src={language} alt="" role="presentation"/>
    </div>
  </div>;
};

const navItems = [
  {
    index: 1,
    path: RoutesUrl.Chat,
    title: 'Chat',
    image: ChatImg,
    isProUser: false,
    isVerbalizeStoreUser: false
  },
  {
    index: 3,
    path: RoutesUrl.Journals,
    title: 'Journals',
    image: JournalImg,
    isProUser: false
  },
  {
    index: 4,
    path: RoutesUrl.Backpack,
    title: 'Backpack',
    image: FavoritesImg,
    isProUser: false
  },
  {
    index: 6,
    path: RoutesUrl.Library,
    title: 'Library',
    image: LibraryImg,
    isProUser: false
  },
  {
    index: 7,
    path: RoutesUrl.Resource,
    title: 'Resource',
    image: ResourceImg,
    isProUser: false
  }
];

interface INavigationProps {
  className?: string;
  proUser?: boolean;
  verbalizeStoreUser?: boolean;
  onOpenMenu: () => void;
}

export const Navigation = ({
  className,
  proUser,
  verbalizeStoreUser,
  onOpenMenu
}: INavigationProps): JSX.Element => {
  const {
    translationKeys
  } = useSelector((state: IStore) => state.translation);

  const location = useLocation();
  const dispatch = useDispatch();

  function setActiveSection(pathname: string) {
    if (location.pathname === RoutesUrl.Content || location.pathname === RoutesUrl.Dialogue) {
      return !!location.state?.parentLocation.includes(pathname.substring(1));
    }

    return location.pathname === pathname;
  }

  function handleMenuItemClick(route: string) {
    return () => {
      if (route === RoutesUrl.Journals && location.pathname !== RoutesUrl.Journals) {
        dispatch(startReloginAction());
      }
      if (!translationKeys.includes(routeTranslationKeys[route])) {
        dispatch(loadTranslation(routeTranslationKeys[route]));
      }
      setActiveSection(route);
      onOpenMenu();
    };
  }

  function handleSetActiveSection(pathname: string) {
    return () => setActiveSection(pathname);
  }

  return (<nav className={cn('nav', className)}>
    {
      navItems.map((item, index) => {
        if (!proUser && item.isProUser) {
          return null;
        }
        if (!verbalizeStoreUser && item.isVerbalizeStoreUser) {
          return null;
        }

        return (<div key={index} className="nav__item">
          <NavLink
            to={item.path}
            className={(nav)=>nav.isActive ? 'nav__link nav__link-active' : 'nav__link'}
            onClick={handleMenuItemClick(item.path)}
            // isActive={handleSetActiveSection(item.path)}
          >
            <div className="nav__item-wrapper">
              <div className="nav__img-wrapper">
                <img className="nav__img" src={item.image} alt="" role="presentation"/>
              </div>
              <p className="nav__item-text">
                <FormattedMessage
                  id={`gritx.common.navigation.${item.title}`}
                  defaultMessage={item.title}
                />
              </p>
            </div>
          </NavLink>
        </div>);
      })
    }
  </nav>);
};
